// ============================================================================
// Landing pública do tenant: /kb/:tenantSlug
// Lista todos os spaces que contêm páginas públicas publicadas.
// F3 Bloco A — Wiki pública.
// ============================================================================

import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, FileText, Loader2 } from "lucide-react";
import { useKbPublicTenant, useKbPublicSpaces } from "@/hooks/kb/useKbPublic";
import { KbPublicLayout } from "./KbPublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Página inicial pública da Base de Conhecimento de um tenant.
 * Renderizada sem autenticação; alimenta SEO com título/descrição do tenant.
 */
export default function KbPublicTenantPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const { data: tenant, isLoading, error } = useKbPublicTenant(tenantSlug);
  const { data: spaces = [] } = useKbPublicSpaces(tenantSlug);

  const canonical = `${window.location.origin}/kb/${tenantSlug}`;
  const title = tenant
    ? `${tenant.tenant_name} — Base de Conhecimento`
    : "Base de Conhecimento";
  const description = tenant
    ? `Explore ${tenant.pages_count} artigos públicos da base de conhecimento de ${tenant.tenant_name}.`
    : "Base de conhecimento pública.";

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <KbPublicLayout tenantSlug={tenantSlug}>
        <Helmet>
          <title>Base de Conhecimento não encontrada</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="rounded-lg border border-border bg-muted/30 p-8 text-center">
          <h1 className="text-2xl font-semibold">Nada por aqui</h1>
          <p className="mt-2 text-muted-foreground">
            Não encontramos uma base pública para <strong>{tenantSlug}</strong>.
          </p>
        </div>
      </KbPublicLayout>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: title,
    url: canonical,
    description,
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <KbPublicLayout
        tenantSlug={tenantSlug}
        tenantName={tenant.tenant_name}
        breadcrumbs={[{ label: "Início" }]}
      >
        <div className="mb-8">
          <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
            <BookOpen className="h-8 w-8 text-primary" aria-hidden />
            Base de Conhecimento
          </h1>
          <p className="mt-2 text-muted-foreground">
            {tenant.spaces_count} {tenant.spaces_count === 1 ? "espaço" : "espaços"} ·{" "}
            {tenant.pages_count} {tenant.pages_count === 1 ? "artigo" : "artigos"}{" "}
            publicados.
          </p>
        </div>

        {spaces.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
            Nenhum conteúdo público disponível ainda.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {spaces.map((s) => (
              <Link
                key={s.space_id}
                to={`/kb/${tenantSlug}/${s.space_slug}`}
                className="group"
              >
                <Card className="h-full transition-shadow hover:shadow-md">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <FileText className="h-5 w-5" aria-hidden />
                      {s.space_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {s.description && (
                      <p className="line-clamp-3 text-sm text-muted-foreground">
                        {s.description}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-muted-foreground">
                      {s.pages_count} {s.pages_count === 1 ? "artigo" : "artigos"}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </KbPublicLayout>
    </>
  );
}
